/**
 * Edit Provider Phone Dialog
 *
 * Dialog for super admins to edit or remove a provider's mobile phone number.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useSnackbar } from 'notistack';
import { useUpdateProviderProfileMutation } from '../store/hooks';

interface EditProviderPhoneDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerName: string;
  currentPhone: string | null | undefined;
}

export function EditProviderPhoneDialog({
  open,
  onClose,
  providerId,
  providerName,
  currentPhone,
}: EditProviderPhoneDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [phone, setPhone] = useState(currentPhone || '');
  const [updateProfile, { isLoading }] = useUpdateProviderProfileMutation();

  // Reset phone when dialog opens with new data
  useEffect(() => {
    if (open) {
      setPhone(currentPhone || '');
    }
  }, [open, currentPhone]);

  const isValidPhone = phone === '' || matchIsValidTel(phone);
  const hasChanges = phone !== (currentPhone || '');
  const isClearing = currentPhone && phone === '';

  const handleSave = useCallback(async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    // Validate phone if provided
    if (phone && !matchIsValidTel(phone)) {
      enqueueSnackbar('Please enter a valid phone number', { variant: 'error' });
      return;
    }

    try {
      await updateProfile({
        providerId,
        updateProviderProfileRequest: {
          mobile_phone_num: phone || null,
        },
      }).unwrap();

      enqueueSnackbar(
        phone ? 'Phone number updated successfully' : 'Phone number removed successfully',
        { variant: 'success' }
      );
      onClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || error?.data?.detail || 'Failed to update phone number',
        { variant: 'error' }
      );
    }
  }, [providerId, phone, hasChanges, updateProfile, enqueueSnackbar, onClose]);

  const handleClose = useCallback(() => {
    setPhone(currentPhone || '');
    onClose();
  }, [currentPhone, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Edit Mobile Phone Number</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Provider
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {providerName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ID: {providerId}
            </Typography>
          </Box>

          <MuiTelInput
            label="Mobile Phone Number"
            value={phone}
            onChange={setPhone}
            disabled={isLoading}
            fullWidth
            defaultCountry="US"
            forceCallingCode
            helperText={
              phone && !isValidPhone
                ? 'Please enter a valid phone number'
                : 'Enter phone number in E.164 format (e.g., +1 555 123 4567) or leave empty to remove'
            }
            error={phone !== '' && !isValidPhone}
          />

          {isClearing && (
            <Alert severity="warning">
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600 }}>
                Removing phone number
              </Typography>
              <Typography variant="body2">
                This will remove the mobile phone number from this provider's profile. They will no
                longer receive SMS notifications at this number.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color={isClearing ? 'warning' : 'primary'}
          disabled={!hasChanges || isLoading || (phone !== '' && !isValidPhone)}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : isClearing ? (
            'Remove Phone'
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
